/* eslint-disable @typescript-eslint/no-explicit-any */
import { MAX_FILE_SIZE_ALLOWED_IN_BYTES, ONE_MB_IN_BYTES, REGEX_PATTERNS, LOCAL_STORAGE_KEYS } from '@/app.constants';
import abilityMapping, { HARD_CODED_CLIENT_PERMISSIONS } from '@/config/acl/ability-mapping';
import { disconnectFromPusher } from '@/config/network/RealTime';
import toast from '@/config/react-toast';
// import { store } from '@/config/redux';
import { primaryMenuItems } from '@/layouts/layouts.constants';
import { iFilterType, iNavbarItem, iPermissionObj, iPermissionsStruct, iPrimaryNavbarItem, iRoleObj, iSecondaryNavbarChild } from '@/types/app.types';
import { clsx, type ClassValue } from 'clsx';
import { FieldValues, Path, RegisterOptions, UseFormRegisterReturn, UseFormReturn } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getLocalStorageItem(key: string): unknown {
  return JSON.parse(localStorage.getItem(key) || 'null');
}

export function setLocalStorageItem(key: string, val: unknown) {
  return localStorage.setItem(key, JSON.stringify(val));
}

export function findNestedValue(obj: Record<string, unknown>, key: string) {
  const keys = key.split('.');
  let currentObj: Record<string, unknown> | unknown = obj;

  for (const k of keys) {
    if (!currentObj) return null;
    if (typeof currentObj === 'object') {
      if (k in currentObj) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        currentObj = currentObj[k];
      } else {
        return null;
      }
    } else {
      // The value is not further iterable
      return currentObj;
    }
  }
  return currentObj;
}

export const updateUserPermissions = (roles: iRoleObj[]) => {
  let permissions: iPermissionsStruct[] = [];
  roles.forEach((role) => {
    // TODO: Temporary solution for now keeping permissions at FE
    if (role.name === 'shipping_client') {
      permissions = permissions.concat(HARD_CODED_CLIENT_PERMISSIONS);
    }
    else if (role.name === 'admin') {
      permissions = permissions.concat(role.permissions);
    } else {
      permissions = permissions.concat(role.permissions);
    }
  });
  const userPermissions: iPermissionObj[] = [];
  permissions.forEach((p) => {
    userPermissions.push({ action: p.action, subject: p.entity });
  });
  return userPermissions;
};

const checkItemPermission = (
  item: iNavbarItem | iSecondaryNavbarChild,
  userPermissions: iPermissionObj[],
  shouldIncludeModuleViewPermission: boolean
): boolean => {
  if (item.checkIsPermitted) {
    return item.checkIsPermitted();
  }

  return item.permissions.some((navItemPermissionObj: iPermissionObj) => {
    return [
      ...userPermissions,
      ...(shouldIncludeModuleViewPermission ? [{
        action: abilityMapping.module.view.action,
        subject: abilityMapping.module.view.subject,
      }] : []),
    ].some((userPermissionObj) => {
      // Direct permission check without calling the exported checkPermission repeatedly
      // since dealing with checkIsPermitted logic here properly
      return userPermissionObj.action === navItemPermissionObj.action && userPermissionObj.subject === navItemPermissionObj.subject;
    });
  });
};

export const filterNavItemChildren = (filteredSecNavItems: iSecondaryNavbarChild[], userPermissions: iPermissionObj[]) => {
  // TODO: Added module view permission for partner based on user permissions
  const shouldIncludeModuleViewPermission = userPermissions.some((userPermissionObj) => {
    return userPermissionObj.action !== abilityMapping.client.can.action && userPermissionObj.subject !== abilityMapping.client.can.subject;
  });

  return filteredSecNavItems.map((navItem) => {
    const tempNavItem = { ...navItem };

    // Check if the nav item has children to filter
    if (navItem.children && Array.isArray(navItem.children)) {
      const filteredChildren = navItem.children.filter((childItem: any) => {
        // If checkIsPermitted exists for child item, use it instead of permission checks
        if (childItem.checkIsPermitted) {
          const isPermitted = childItem.checkIsPermitted();
          return isPermitted;
        }

        // Check if child has permissions array
        if (!childItem.permissions || !Array.isArray(childItem.permissions)) {
          return false; // If no permissions defined, deny access
        }

        const hasPermission = childItem.permissions.some((childPermissionObj: any) => {
          return [
            ...userPermissions,
            ...(shouldIncludeModuleViewPermission ? [{
              action: abilityMapping.module.view.action,
              subject: abilityMapping.module.view.subject,
            }] : []),
          ].some((userPermissionObj) => {
            return userPermissionObj.action === childPermissionObj.action && userPermissionObj.subject === childPermissionObj.subject;
          });
        });
        return hasPermission;
      });

      // Update the nav item with filtered children
      tempNavItem.children = filteredChildren;

      // If no children remain after filtering, you might want to handle this case
      // For now, we'll keep the parent even if it has no accessible children
    }

    return tempNavItem;
  });
};

export const getFilteredPNavItems = (userPermissions: iPermissionObj[]) => {
  // TODO: Added module view permission for partner based on user permissions
  const shouldIncludeModuleViewPermission = userPermissions.some((userPermissionObj) => {
    return userPermissionObj.action !== abilityMapping.client.can.action && userPermissionObj.subject !== abilityMapping.client.can.subject;
  });

  const filteredPNavItems: iPrimaryNavbarItem[] = primaryMenuItems.filter((navItem) => {
    return checkItemPermission(navItem, userPermissions, shouldIncludeModuleViewPermission);
  });

  const pNavItemsFiltered = filteredPNavItems.map((navItem) => {
    const tempNavItem = { ...navItem };
    if (navItem.secondaryNavbar && !(navItem.secondaryNavbar.hasCustomSecondaryNavbar === true)) {

      // 1. Filter the top-level items of the secondary navbar (Groups or Flat items)
      // This is the "Level 1" check.
      const filteredSecNavItemsLevel1 = navItem.secondaryNavbar.children.filter((secNavItem) => {
        return checkItemPermission(secNavItem, userPermissions, shouldIncludeModuleViewPermission);
      });

      // 2. Filter the children of the secondary navbar items (Level 2 items inside Groups)
      // We also filter out any groups that ended up empty after their children were filtered.
      const tempChildren = filterNavItemChildren(filteredSecNavItemsLevel1, userPermissions).filter((item) => {
        if (item.children && Array.isArray(item.children)) {
          return item.children.length > 0;
        }
        return true;
      });

      if (tempChildren.length > 0) {
        tempNavItem.secondaryNavbar = { ...navItem.secondaryNavbar, children: tempChildren };

        // Resolve the correct navLink.
        // It should be the link of the first available actionable item.
        // If the first item is a Group (has children), take its first child's link.
        // If the first item is a Flat item (no children), take its own link.
        const firstItem = tempChildren[0];
        const resolvedLink = (firstItem.children && firstItem.children.length > 0)
          ? firstItem.children[0].navLink
          : firstItem.navLink;

        tempNavItem.secondaryNavbar.navLink = resolvedLink;
        tempNavItem.navLink = resolvedLink;
      }
    }
    return tempNavItem;
  });
  return pNavItemsFiltered;
};

export const getError = (errors: Record<string, unknown>, key: string): string | null => {
  const errorObj = findNestedValue(errors, key) as Record<string, unknown> | null;
  if (errorObj && errorObj.message) {
    if (typeof errorObj.message === 'string') return errorObj.message;
  }
  return null;
};

export function doRegister<CustomFormFields extends FieldValues>(
  nameKey: Path<CustomFormFields>,
  formObj: UseFormReturn<CustomFormFields>,
  options?: RegisterOptions
): { registeredField: UseFormRegisterReturn<string>; error: string | null } {
  const {
    register,
    formState: { errors },
  } = formObj;
  const name = register(nameKey, options);
  const error = getError(errors, nameKey);
  return {
    registeredField: name,
    error,
  };
}

export const handleConvertTimeToHumanReadable = (timeToBeConverted: string) => {
  const [time, offset] = timeToBeConverted.split('+');
  const timeString = time.replace('T', ' ');

  // Construct a new date string by appending the UTC offset
  const dateStringWithOffset = timeString + ` UTC${offset}`;

  // Create a Date object with the adjusted date string
  const date = new Date(dateStringWithOffset);

  // Format the time in a human-readable form
  const humanReadableTime = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return humanReadableTime;
};

export const convertTimeTo12HourFormat = (timeToBeConverted: string): string => {
  // Split the input time string into time and offset
  const [time, offset] = timeToBeConverted.split('+');

  // Create a Date object with the time string and UTC offset
  const date = new Date(`2000-01-01T${time}+${offset}`);

  // Format the time in a 12-hour clock with AM/PM
  const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
  const formattedTime = date.toLocaleTimeString('en-US', options);

  return formattedTime;
};

// pass multiple of 10 as much you want the number to be 
export const roundNumberUpto = (num: number, places: number) => {
  return (Math.round(num * places) / places);
};

export const formattedLongDate = (date: string | number | Date) => {
  const issuedDate = new Date(date);
  const dateOptions: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true };
  const formattedDate = issuedDate?.toLocaleString('en-US', dateOptions);

  return formattedDate;
};

export function convertToUTC(inputTime: string) {
  // Assuming inputTime is in the format "23:59"
  // const inputFormat = "HH:mm";

  // Create a Date object with a dummy date (e.g., today's date)
  const dummyDate = new Date();
  const inputDateTime = new Date(`${dummyDate.toISOString().slice(0, 10)}T${inputTime}`);

  // Calculate the local timezone offset in minutes
  const localTimezoneOffset = dummyDate.getTimezoneOffset();

  // Convert to UTC by subtracting the local timezone offset
  const utcDateTime = new Date(inputDateTime.getTime() - localTimezoneOffset * 60000);

  // Format the result as "hh:mm:ss+0000"
  const resultUTC = utcDateTime.toISOString().slice(11, 19) + '+0000';

  return resultUTC;
}

export const getApiUrl = (url: string, params: { [x: string]: any; }) => {
  for (const key in params) {
    url = url.replace(`:${key}`, params[key]);
  }
  return url;
};

export const getFiltersBasedOnOmit = (filters: iFilterType) => {
  return Object.values(filters).filter(filter => !filter.omit);
};

export const downloadFile = (fileLink: string) => {
  const anchor = document.createElement("a")
  anchor.href = fileLink
  anchor.download = fileLink
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

// pusher global callback function

let toastId: any
export const pusherCallback = (response: any) => {
  const { event_type, is_last_response, data, actions } = response
  const messageSeparator = data?.message?.content.split('|') || []
  const messageContent = messageSeparator[0]
  const messageDesc = messageSeparator.length > 1 ? messageSeparator[messageSeparator.length - 1] : ''
  const messageType = data?.message?.type

  // deciding and executing the ui interaction depending on type
  switch (event_type) {
    case "TOAST":
      // store.dispatch(handlePusherResponse({ [event_name]: messageType }))
      toast(messageContent, { type: messageType === 'failure' ? 'error' : messageType }, messageDesc ? messageDesc : '')
      break
    case "DOWNLOAD_LINK":
      // store.dispatch(handlePusherResponse({ [event_name]: messageType }))
      downloadFile(data.link)
      if (data.message) toast(messageContent, { type: messageType === 'failure' ? 'error' : messageType }, messageDesc ? messageDesc : '')
      break
    case "CUSTOM":
      actions.forEach((el: any) => {
        const sequence = el.sequence
        const selectedAction = actions[sequence - 1]
        const actionType = selectedAction.action_type
        const url = selectedAction.data.url
        const content = selectedAction.data.message.content
        const contentType = selectedAction.data.message.content_type
        switch (actionType) {
          case "TOAST":
            // store.dispatch(handlePusherResponse({ [event_name]: contentType }))
            toastId = toast(content, { type: contentType === 'failure' ? 'error' : contentType, id: toastId }, messageDesc ? messageDesc : '')
            break
          case "DOWNLOAD":
            // store.dispatch(handlePusherResponse({ [event_name]: contentType }))
            toastId = toast(content, { type: contentType === 'failure' ? 'error' : contentType, id: toastId }, messageDesc ? messageDesc : '')
            downloadFile(url)
            break
          case "INFO":
            // store.dispatch(handlePusherResponse({ [event_name]: contentType }))
            toastId = toast(content, { type: contentType === 'failure' ? 'error' : contentType, id: toastId }, messageDesc ? messageDesc : '')
            break
          default:
        }
      })
      break
    default:
    // do nothing
  }

  // closing connection if this is the last response
  if (is_last_response) {
    disconnectFromPusher()
  }
}

export const paramsSerializer = (params: any) => {
  const parts = []
  for (const key in params) {
    if (Array.isArray(params[key])) {
      for (const value of params[key]) {
        if (value || typeof value === "boolean") {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        }
      }
    } else {
      if (params[key] || typeof params[key] === "boolean") {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      }
    }
  }
  const searchParams = parts.join("&")
  return searchParams
}

type NavElementType = 'primary-icon' | 'primary-text' | 'secondary-icon' | 'secondary-text' | 'secondary-icon-without-children';

type iElementColorProps = {
  elementType: NavElementType;
  isActive: boolean;
  isHovering: boolean;
}

export const getElementColor = ({ elementType, isActive, isHovering }: iElementColorProps): string => {
  if (isActive) {
    switch (elementType) {
      case 'primary-icon':
        return 'var(--white)';
      case 'primary-text':
        return 'var(--primary)';
      case 'secondary-icon':
        return 'var(--dark)';
      case 'secondary-text':
        return 'var(--dark)';
      case 'secondary-icon-without-children':
        return 'var(--white)';

    }
  }

  if (isHovering) {
    return 'var(--dark)';
  }

  return 'var(--dark_6)';
};

export const checkPermission = ({ hasPermission, checkIsPermitted }: { hasPermission: boolean; checkIsPermitted?: () => boolean }) => {
  if (typeof checkIsPermitted === 'function') return checkIsPermitted();
  return hasPermission;
};

export function deepCopy(obj: any, hash = new WeakMap()) {
  // Return primitives and functions as they are
  if (Object(obj) !== obj || typeof obj === 'function') return obj;
  // Return memoized copy if the object has been copied before to handle circular references
  if (hash.has(obj)) return hash.get(obj);

  // Handle Date
  if (obj instanceof Date) return new Date(obj);
  // Handle RegExp
  if (obj instanceof RegExp) return new RegExp(obj);

  // Handle arrays
  const copy = Array.isArray(obj) ? [] : Object.create(Object.getPrototypeOf(obj));

  // Save the copy in the hash map to handle circular references
  hash.set(obj, copy);

  // Copy properties including symbol properties
  const keys = Reflect.ownKeys(obj);
  for (const key of keys) {
    copy[key] = deepCopy(obj[key], hash);
  }

  return copy;
}

export const validateTags = (str: string) => {
  return REGEX_PATTERNS.HTML_TAGS.test(str);
};

export const checkHtmlInjection = (str: string) => {
  const value = str || ''
  if (value.trim()) {
    // after this checks for the valid tags if found return true
    if (validateTags(value)) {
      return true
    }

    //check for HTML in dom if found then return true
    const parsedHTML = new DOMParser().parseFromString(value, 'text/html');
    const hasHtmlElements = Array.from(parsedHTML.body.childNodes).some(
      (n) => n.nodeType === Node.ELEMENT_NODE
    );

    if (hasHtmlElements) {
      return true
    }
  }
  return false
}

export const isFileTypeAndSizeValid = (selectedFile: File, fileTypes: string[], maxFileSizeAllowed: number) => {
  if (!maxFileSizeAllowed) maxFileSizeAllowed = MAX_FILE_SIZE_ALLOWED_IN_BYTES
  if (selectedFile && selectedFile.size > maxFileSizeAllowed) {
    toast(`Please upload a file of less then ${maxFileSizeAllowed / ONE_MB_IN_BYTES} Mb.`, { type: 'error' })
    return false
  }
  if (selectedFile && !fileTypes.includes(selectedFile.type)) {
    toast('Please upload a file in a supported format.', { type: 'error' })
    return false
  }
  return true
}

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj: Record<string, unknown>) => Object.keys(obj).length === 0

export const commaSeparateInternational = (num: number, requireIntegerFormat = false) => {
  if (isNaN(num)) return null

  if (requireIntegerFormat) {
    // Round the number to the nearest integer
    const roundedNum = Math.round(num);

    // Comma separate the integer part according to international numbering system
    const formattedNumber = roundedNum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return formattedNumber
  } else {
    // Convert number to string and split into integer and decimal parts
    const [integerPart, decimalPart] = num.toString().split('.')

    // Comma separate the integer part according to international numbering system
    const commaSeparatedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    // Combine integer part and decimal part (if any)
    const formattedNumber = decimalPart ? `${commaSeparatedInteger}.${decimalPart}` : commaSeparatedInteger

    return formattedNumber
  }
}

export const pluralizeUnit = (value: number, unit: string) => value > 0 ? `${value} ${value > 1 ? unit + 's' : unit}` : '';

export const validateDecimals = (val: string, decimals = 2) => {
  if (val === '') return true // Allow empty string
  let value = val
  if (typeof value === 'number') value = val.toString()
  const regex = new RegExp(`^-?\\d*(\\.\\d{0,${decimals}})?$`)
  return regex.test(value)
}

export const validatePositiveNumber = (val: string) => {
  if (val === '') return true // Allow empty string
  const regex = /^\d+(\.\d*)?$/
  return regex.test(val)
}

export const isUserLoggedIn = () => getLocalStorageItem(LOCAL_STORAGE_KEYS.AUTH) as { access_token: string } | null;

